import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Heart, ArrowLeft, ArrowRight, Loader2, CheckCircle } from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Progress } from "../components/ui/progress";
import axios from "axios";

const API = process.env.REACT_APP_BACKEND_URL;

// Questions du diagnostic séparation
const QUESTIONS_FR = [
  {
    id: 1,
    category: "identite",
    text: "Depuis la séparation, j'ai l'impression de ne plus vraiment savoir qui je suis.",
    options: [
      { value: 1, label: "Pas du tout" },
      { value: 2, label: "Un peu" },
      { value: 3, label: "Moyennement" },
      { value: 4, label: "Beaucoup" },
      { value: 5, label: "Tout à fait" }
    ]
  },
  {
    id: 2,
    category: "identite",
    text: "Je me demande souvent ce que je vaux maintenant que je suis seul(e).",
    options: [
      { value: 1, label: "Jamais" },
      { value: 2, label: "Rarement" },
      { value: 3, label: "Parfois" },
      { value: 4, label: "Souvent" },
      { value: 5, label: "Très souvent" }
    ]
  },
  {
    id: 3,
    category: "solitude",
    text: "Je ressens une solitude que les autres ne semblent pas comprendre.",
    options: [
      { value: 1, label: "Pas du tout" },
      { value: 2, label: "Un peu" },
      { value: 3, label: "Moyennement" },
      { value: 4, label: "Beaucoup" },
      { value: 5, label: "Tout à fait" }
    ]
  },
  {
    id: 4,
    category: "solitude",
    text: "J'ai l'impression que mes amis ou ma famille n'ont pas le temps pour ce que je traverse.",
    options: [
      { value: 1, label: "Pas du tout" },
      { value: 2, label: "Un peu" },
      { value: 3, label: "Moyennement" },
      { value: 4, label: "Beaucoup" },
      { value: 5, label: "Tout à fait" }
    ]
  },
  {
    id: 5,
    category: "solitude",
    text: "Je me sens décalé(e) par rapport aux autres couples ou familles autour de moi.",
    options: [
      { value: 1, label: "Jamais" },
      { value: 2, label: "Rarement" },
      { value: 3, label: "Parfois" },
      { value: 4, label: "Souvent" },
      { value: 5, label: "Très souvent" }
    ]
  },
  {
    id: 6,
    category: "confiance",
    text: "J'ai peur de ne plus être capable de créer une relation saine à l'avenir.",
    options: [
      { value: 1, label: "Pas du tout" },
      { value: 2, label: "Un peu" },
      { value: 3, label: "Moyennement" },
      { value: 4, label: "Beaucoup" },
      { value: 5, label: "Tout à fait" }
    ]
  },
  {
    id: 7,
    category: "confiance",
    text: "Je doute de ma capacité à m'en sortir et à être heureux(se) seul(e).",
    options: [
      { value: 1, label: "Pas du tout" },
      { value: 2, label: "Un peu" },
      { value: 3, label: "Moyennement" },
      { value: 4, label: "Beaucoup" },
      { value: 5, label: "Tout à fait" }
    ]
  },
  {
    id: 8,
    category: "projection",
    text: "J'ai du mal à me projeter dans l'avenir sans cette relation.",
    options: [
      { value: 1, label: "Pas du tout" },
      { value: 2, label: "Un peu" },
      { value: 3, label: "Moyennement" },
      { value: 4, label: "Beaucoup" },
      { value: 5, label: "Tout à fait" }
    ]
  },
  {
    id: 9,
    category: "projection",
    text: "Je ne sais plus vraiment ce que je veux pour ma vie maintenant.",
    options: [
      { value: 1, label: "Pas du tout" },
      { value: 2, label: "Un peu" },
      { value: 3, label: "Moyennement" },
      { value: 4, label: "Beaucoup" },
      { value: 5, label: "Tout à fait" }
    ]
  },
  {
    id: 10,
    category: "identite",
    text: "J'ai parfois l'impression d'avoir perdu ma place dans le monde.",
    options: [
      { value: 1, label: "Jamais" },
      { value: 2, label: "Rarement" },
      { value: 3, label: "Parfois" },
      { value: 4, label: "Souvent" },
      { value: 5, label: "Très souvent" }
    ]
  }
];

const QUESTIONS_EN = [
  {
    id: 1,
    category: "identite",
    text: "Since the separation, I feel like I no longer really know who I am.",
    options: [
      { value: 1, label: "Not at all" },
      { value: 2, label: "A little" },
      { value: 3, label: "Somewhat" },
      { value: 4, label: "A lot" },
      { value: 5, label: "Completely" }
    ]
  },
  {
    id: 2,
    category: "identite",
    text: "I often wonder what I'm worth now that I'm alone.",
    options: [
      { value: 1, label: "Never" },
      { value: 2, label: "Rarely" },
      { value: 3, label: "Sometimes" },
      { value: 4, label: "Often" },
      { value: 5, label: "Very often" }
    ]
  },
  {
    id: 3,
    category: "solitude",
    text: "I feel a loneliness that others don't seem to understand.",
    options: [
      { value: 1, label: "Not at all" },
      { value: 2, label: "A little" },
      { value: 3, label: "Somewhat" },
      { value: 4, label: "A lot" },
      { value: 5, label: "Completely" }
    ]
  },
  {
    id: 4,
    category: "solitude",
    text: "I feel like my friends or family don't have time for what I'm going through.",
    options: [
      { value: 1, label: "Not at all" },
      { value: 2, label: "A little" },
      { value: 3, label: "Somewhat" },
      { value: 4, label: "A lot" },
      { value: 5, label: "Completely" }
    ]
  },
  {
    id: 5,
    category: "solitude",
    text: "I feel out of sync with other couples or families around me.",
    options: [
      { value: 1, label: "Never" },
      { value: 2, label: "Rarely" },
      { value: 3, label: "Sometimes" },
      { value: 4, label: "Often" },
      { value: 5, label: "Very often" }
    ]
  },
  {
    id: 6,
    category: "confiance",
    text: "I'm afraid I won't be able to create a healthy relationship in the future.",
    options: [
      { value: 1, label: "Not at all" },
      { value: 2, label: "A little" },
      { value: 3, label: "Somewhat" },
      { value: 4, label: "A lot" },
      { value: 5, label: "Completely" }
    ]
  },
  {
    id: 7,
    category: "confiance",
    text: "I doubt my ability to get through this and be happy alone.",
    options: [
      { value: 1, label: "Not at all" },
      { value: 2, label: "A little" },
      { value: 3, label: "Somewhat" },
      { value: 4, label: "A lot" },
      { value: 5, label: "Completely" }
    ]
  },
  {
    id: 8,
    category: "projection",
    text: "I find it hard to envision the future without this relationship.",
    options: [
      { value: 1, label: "Not at all" },
      { value: 2, label: "A little" },
      { value: 3, label: "Somewhat" },
      { value: 4, label: "A lot" },
      { value: 5, label: "Completely" }
    ]
  },
  {
    id: 9,
    category: "projection",
    text: "I no longer really know what I want for my life now.",
    options: [
      { value: 1, label: "Not at all" },
      { value: 2, label: "A little" },
      { value: 3, label: "Somewhat" },
      { value: 4, label: "A lot" },
      { value: 5, label: "Completely" }
    ]
  },
  {
    id: 10,
    category: "identite",
    text: "I sometimes feel like I've lost my place in the world.",
    options: [
      { value: 1, label: "Never" },
      { value: 2, label: "Rarely" },
      { value: 3, label: "Sometimes" },
      { value: 4, label: "Often" },
      { value: 5, label: "Very often" }
    ]
  }
];

export default function SeparationDiagnostic() {
  const { i18n } = useTranslation();
  const navigate = useNavigate();
  const isFrench = i18n.language === 'fr';
  
  const questions = isFrench ? QUESTIONS_FR : QUESTIONS_EN;
  
  const [currentQuestion, setCurrentQuestion] = useState(0);
  const [answers, setAnswers] = useState({});
  const [email, setEmail] = useState("");
  const [showEmailForm, setShowEmailForm] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const progress = ((currentQuestion + 1) / questions.length) * 100;

  const handleAnswer = (value) => {
    const newAnswers = { ...answers, [questions[currentQuestion].id]: value };
    setAnswers(newAnswers);

    if (currentQuestion < questions.length - 1) {
      setTimeout(() => setCurrentQuestion(currentQuestion + 1), 300);
    } else {
      setShowEmailForm(true);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!email.trim()) {
      setError(isFrench ? "Veuillez entrer votre email" : "Please enter your email");
      return;
    }

    setLoading(true);
    setError("");

    try {
      const response = await axios.post(`${API}/api/separation/diagnostic/submit`, {
        email: email.trim(),
        answers,
        language: i18n.language
      });

      navigate(`/separation/results/${response.data.session_id}`);
    } catch (err) {
      console.error("Submit error:", err);
      setError(err.response?.data?.detail || (isFrench ? "Une erreur est survenue" : "An error occurred"));
      setLoading(false);
    }
  };

  const handleBack = () => {
    if (showEmailForm) {
      setShowEmailForm(false);
    } else if (currentQuestion > 0) {
      setCurrentQuestion(currentQuestion - 1);
    } else {
      navigate("/");
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-[#FDF8F7] to-[#F5EEED]">
      {/* Header */}
      <header className="bg-white/80 backdrop-blur-sm border-b border-[#E8E4DE] sticky top-0 z-50">
        <div className="max-w-4xl mx-auto px-4 py-4 flex items-center justify-between">
          <button 
            onClick={handleBack}
            className="flex items-center gap-2 text-[#5C5C5C] hover:text-[#2D2D2D] transition-colors"
            data-testid="back-button"
          >
            <ArrowLeft className="w-5 h-5" />
            {isFrench ? "Retour" : "Back"}
          </button>
          <div className="flex items-center gap-2">
            <Heart className="w-5 h-5 text-[#8B5A6B]" />
            <span className="font-heading text-[#8B5A6B] font-medium">
              {isFrench ? "Après la séparation" : "After separation"}
            </span>
          </div>
        </div>
      </header>

      <main className="max-w-2xl mx-auto px-4 py-8">
        {!showEmailForm ? (
          <>
            {/* Progress */}
            <div className="mb-8">
              <div className="flex justify-between text-sm text-[#8A8A8A] mb-2">
                <span>{isFrench ? `Question ${currentQuestion + 1}/${questions.length}` : `Question ${currentQuestion + 1}/${questions.length}`}</span>
                <span>{Math.round(progress)}%</span>
              </div>
              <Progress value={progress} className="h-2 bg-[#E8E4DE]" />
            </div>

            {/* Question Card */}
            <div className="bg-white rounded-2xl shadow-lg p-8 mb-6" data-testid="question-card">
              <div className="inline-flex items-center gap-2 bg-[#8B5A6B]/10 text-[#8B5A6B] px-3 py-1 rounded-full text-sm mb-4">
                {questions[currentQuestion].category === "identite" && (isFrench ? "Identité" : "Identity")}
                {questions[currentQuestion].category === "solitude" && (isFrench ? "Solitude" : "Loneliness")}
                {questions[currentQuestion].category === "confiance" && (isFrench ? "Confiance" : "Confidence")}
                {questions[currentQuestion].category === "projection" && "Projection"}
              </div>
              
              <h2 className="text-xl md:text-2xl font-heading text-[#2D2D2D] mb-8 leading-relaxed">
                {questions[currentQuestion].text}
              </h2>

              <div className="space-y-3">
                {questions[currentQuestion].options.map((option) => (
                  <button
                    key={option.value}
                    onClick={() => handleAnswer(option.value)}
                    className={`w-full p-4 rounded-xl border-2 text-left transition-all duration-200 ${
                      answers[questions[currentQuestion].id] === option.value
                        ? 'border-[#8B5A6B] bg-[#8B5A6B]/5'
                        : 'border-[#E8E4DE] hover:border-[#8B5A6B]/50 hover:bg-[#FDF8F7]'
                    }`}
                    data-testid={`option-${option.value}`}
                  >
                    <span className="text-[#2D2D2D]">{option.label}</span>
                  </button>
                ))}
              </div>
            </div>
          </>
        ) : (
          /* Email Form */
          <div className="bg-white rounded-2xl shadow-lg p-8" data-testid="email-form">
            <div className="text-center mb-8">
              <div className="w-16 h-16 bg-[#8B5A6B]/10 rounded-full flex items-center justify-center mx-auto mb-4">
                <CheckCircle className="w-8 h-8 text-[#8B5A6B]" />
              </div>
              <h2 className="text-2xl font-heading text-[#2D2D2D] mb-2">
                {isFrench ? "Diagnostic terminé !" : "Diagnostic complete!"}
              </h2>
              <p className="text-[#5C5C5C]">
                {isFrench 
                  ? "Entre ton email pour recevoir ton analyse personnalisée"
                  : "Enter your email to receive your personalized analysis"}
              </p>
            </div>

            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <Input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder={isFrench ? "ton@email.com" : "your@email.com"}
                  className="w-full text-center text-lg py-6"
                  data-testid="email-input"
                  required
                />
              </div>

              {error && (
                <div className="bg-red-50 text-red-600 px-4 py-3 rounded-lg text-sm text-center">
                  {error}
                </div>
              )}

              <Button
                type="submit"
                disabled={loading}
                className="w-full bg-gradient-to-r from-[#8B5A6B] to-[#A67B8B] hover:opacity-90 text-white py-6 rounded-full text-lg"
                data-testid="submit-button"
              >
                {loading ? (
                  <>
                    <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                    {isFrench ? "Analyse en cours..." : "Analyzing..."}
                  </>
                ) : (
                  <>
                    {isFrench ? "Découvrir mon analyse" : "Discover my analysis"}
                    <ArrowRight className="w-5 h-5 ml-2" />
                  </>
                )}
              </Button>

              <p className="text-center text-xs text-[#8A8A8A]">
                {isFrench 
                  ? "Ton email ne sera jamais partagé. Un seul diagnostic gratuit par email."
                  : "Your email will never be shared. One free diagnostic per email."}
              </p>
            </form>
          </div>
        )}

        {/* Quote */}
        <div className="mt-8 p-6 bg-[#8B5A6B]/5 rounded-xl border border-[#8B5A6B]/20">
          <p className="text-center text-[#5C5C5C] italic leading-relaxed">
            {isFrench 
              ? "« Après une séparation, on ne souffre pas seulement de la rupture. On souffre de ne plus savoir où est sa place dans un monde qui a changé. »"
              : "\"After a separation, we don't just suffer from the breakup. We suffer from no longer knowing where our place is in a world that has changed.\""}
          </p>
        </div>
      </main>
    </div>
  );
}
